import { logEvent } from "firebase/analytics";
import { addDoc, collection, deleteDoc, doc, DocumentReference, FieldPath, getDoc, onSnapshot, query, setDoc, Timestamp, where, documentId, orderBy } from "firebase/firestore";
import moment from "moment";
import React, { useContext, useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "./Button";
import { FirebaseContext } from "./context";
import { capitalizeFirstLetter } from "./funcs";
import { FirestoreConverter, IComment, IEvent, INews, IUserProfile } from "./models";
import { Photo } from "./Photo";
import { RWebShare } from "react-web-share";
import { Event } from './Event';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm'
import { Loader } from "./Loader";

export const PageEvent = () => {
    const params = useParams();
    const navigate = useNavigate();
    const commentRef = useRef<HTMLDivElement>(null);
    const { db, user, analytics } = useContext(FirebaseContext);
    const [ event, setEvent ] = useState<IEvent | undefined>(undefined);
    const [ participants, setParticipants ] = useState<string[]>([]);
    const [ comments, setComments ] = useState<IComment[]>([]);
    const [ commentsUsers, setCommentsUsers ] = useState<{[key: string] : IUserProfile}>({});
    const [ commentValue, setCommentValue ] = useState("");
    const [ shared, setShared ] = useState(0);
    const [ warnShown, setWarnShown ] = useState(false);
    const [loading, setLoading] = useState(true);
    const outdated = event ? Date.now() > event?.end.toMillis() : false;
    useEffect(() => {
        if (db) {
            return onSnapshot(doc(db, `/v2-events/${params.id}`), (snapshot) => {
                if(snapshot.exists()){
                    const d = { ...snapshot.data(), id: snapshot.id } as IEvent;
                    setEvent(d);
                }
                else{
                    setEvent(undefined);
                }
                setLoading(false);
            });
        }
    }, [db, params.id]);
    useEffect(() => {
        if (db) {
            return onSnapshot(collection(db, 'v2-events', params.id as string, 'shared'), (snapshot) => setShared(snapshot.docs.length));
        }
    }, [db, params.id]);
    useEffect(() => {
        if (db) {
            return onSnapshot(query(collection(db, 'v2-events', params.id as string, 'ps'), where("public", '==', true)), (snapshot) => setParticipants(snapshot.docs.map((l) => l.id)));
        }
    }, [db, params.id]);
    useEffect(() => {
        if (db && comments.length > 0) {
            return onSnapshot(query(collection(db, 'v2-users'), where(documentId(), 'in', comments.map((c) => c.author))), (snapshot) => {
                let users = {} as {[key: string]: IUserProfile};
                snapshot.docs.forEach((d) => users[d.id] = d.data() as IUserProfile);
                setCommentsUsers(users);
            });
        }
    }, [db, comments]);
    useEffect(() => {
        if (db) {
            return onSnapshot(query(collection(db, 'v2-events', params.id as string, 'comments'), orderBy('date', 'desc')), (snapshot) => {
                setComments(snapshot.docs.map((l) => {
                    return { ...l.data(), id: l.id } as IComment;
                }));
            });
        }
    }, [db, params.id]);
    return <div className="padding-x py-10">
        <Loader loading={loading} />
        { event ? 
        <div className="flex gap-10 flex-wrap justify-center">
            <div className="mb-10">
                <div className="justify-center flex">
                    <Photo src={event.image!} alt={event.title} size='large' variant="square" />
                </div>
                <div className="flex gap-10 justify-center mt-10">
                    <div className='flex gap-5'>
                        <p className='text-gray-500 text-lg'>{participants.length}</p>
                        <div className="cursor-pointer" onClick={() => {
                            if(user && event.start.toMillis() <= Date.now()){
                                if(!(participants.indexOf(user.uid) >= 0)){
                                    logEvent(analytics!, 'participate', {
                                        content_type: 'event',
                                        content_id: params.id,
                                        user_id: user.uid,
                                        authorized: true
                                    });
                                    console.log("PS!")
                                    setDoc(doc(db!, 'v2-events', params.id as string, 'ps', user!.uid as string), {
                                        public: true,
                                        date: Timestamp.now()
                                    });
                                }
                                else{
                                    console.log("UnPS!")
                                    deleteDoc(doc(db!, 'v2-events', params.id as string, 'ps', user!.uid as string));
                                }
                            }
                            else{
                                logEvent(analytics!, 'participate', {
                                    content_type: 'event',
                                    content_id: params.id,
                                    authorized: false
                                });
                                navigate('/me');
                            }
                        }}>
                            {
                                user && (participants.indexOf(user.uid) >= 0) ?
                                <i className="fa-solid fa-user fa-2x text-[#01487C]"></i>
                                :
                                <i className="fa-regular fa-user fa-2x text-[#01487C]"></i>
                            }
                        </div>
                    </div>
                    <div className="flex gap-5">
                        <p className='text-gray-500 text-lg'>{comments.length}</p>
                        <i className="fa-solid fa-comment fa-2x text-[#01487C] cursor-pointer" onClick={() => {
                            if(user)
                                commentRef.current?.scrollIntoView();
                            else 
                                navigate('/me');
                        }}></i>
                    </div>
                    <div className="flex gap-5">
                        <p className='text-gray-500 text-lg'>{shared}</p>
                        <RWebShare
                            data={{
                                text: `Мероприятие "${event.title}"`,
                                url: document.URL,
                                title: "Дорогой Добра"
                            }}
                            onClick={() => {
                                logEvent(analytics!, 'share', {
                                    content_type: 'event',
                                    content_id: params.id,
                                });
                                if(user)
                                    setDoc(doc(db!, 'v2-events', params.id as string, 'shared', user?.uid), {
                                        date: Timestamp.now()
                                    });
                            }}
                        >
                            <i className="fa-solid fa-share fa-2x text-[#01487C] cursor-pointer"></i>
                        </RWebShare>
                    </div>
                </div>
            </div>
            <div>
                <div className='max-w-xl'>
                    {outdated ? <div className="bg-[#01487C] text-white rounded-full px-3 py-1 text-sm w-fit mb-2">Прошедшее</div> : null}
                    <p className="text-lg text-gray-500">{moment(event.start.toMillis()).format('hh:mm DD.MM.YY')} - {moment(event.end.toMillis()).format('hh:mm DD.MM.YY')}</p>
                    <p className="text-4xl mt-3">{event.title}</p>
                    { event.description ? <div className="mt-7 appear space-y-5"><p>{event.description}</p></div> : null }
                </div>
                <div className='w-full h-0.5 bg-gray-300 my-10'></div>
                {
                    user ? <div className="">
                        <p className="text-xl mb-5" ref={commentRef}>Поделитесь своим мнением</p>
                        <textarea className="border-[#01487C] border-[3px] w-full p-5" wrap='soft' value={commentValue} onChange={(ev) => {
                            setCommentValue(ev.target.value);
                            setWarnShown(false);
                        }} placeholder="Введите текст комментария"></textarea>
                        <div></div>
                        <div className='flex gap-5'>
                            <Button label="Отправить" onClick={() => {
                                if(commentValue.trim().length > 0){
                                    addDoc(collection(db!, 'v2-events', params.id as string, 'comments'), {
                                        author: user!.uid,
                                        date: Timestamp.now(),
                                        text: commentValue.trim()
                                    });
                                    setCommentValue("");
                                }
                                else{
                                    setWarnShown(true);
                                }
                            }}/>
                            {warnShown ? <p>Комментарий не может быть пустым</p> : null}
                        </div>
                    </div>
                    :
                    <div>
                        <p className="mb-5">Войдите в аккаунт для комментирования</p>
                        <Button label="Войти" onClick={() => navigate('/me')}/>
                    </div>
                }
                <div className="mt-10 space-y-5">
                    {
                        comments.length > 0 ?
                            <>
                                {comments.map((c) => commentsUsers[c.author] ? <div key={c.id}>
                                    <div className="flex gap-7">
                                        <div>
                                            <div className='cursor-pointer' onClick={() => navigate(`/user/${c.author}`)}>
                                                <Photo src={commentsUsers[c.author]!.avatar} size='tiny'/>
                                            </div>
                                            {user && c.author === user.uid ? <div className="flex justify-center mt-7 mb-3">
                                                    <i className="fa-solid fa-trash-can fa-lg text-gray-500 cursor-pointer" onClick={() =>{
                                                        deleteDoc(doc(db!, 'v2-events', params.id as string, 'comments', c.id as string));
                                                    }}></i>
                                                </div>
                                            : null}
                                        </div>
                                        <div className='space-y-1'>
                                            <div className="flex gap-5">
                                                <p className="text-xl max-w-lg cursor-pointer" onClick={() => navigate(`/user/${c.author}`)}>{commentsUsers[c.author]!.name}</p>
                                                <p className="text-gray-500">{c.date ? moment(c.date.toMillis()).fromNow() : null}</p>
                                            </div>
                                            <p className="max-w-xl">{c.text}</p>
                                        </div>
                                    </div>
                                </div>
                                :
                                <div>
                                    <p>Загрузка комментария</p>
                                </div>
                                )}
                            </>
                        :
                        null
                        // <p>Нет комментариев</p> 
                    }
                </div>
            </div>
        </div>
        : <div className='flex justify-center gap-10 flex-wrap'>
            <div>
                <i className="fa-solid fa-calendar-days fa-7x text-[#01487C]"></i>
            </div>
            <div className="flex flex-col justify-center">
                <p className="text-3xl">Вы нашли внеземное мероприятие</p>
                <p className="text-xl mb-10">Мероприятие удалено или не существует</p>
                <Button label="На главную" onClick={() => navigate('/')} />
            </div>
        </div> }
    </div>
}